import { useState } from 'react';
import { X, FileJson, Database, FileCode } from 'lucide-react';

const ImportSelectorModal = ({ onClose, onSelectType }) => {
  const [selectedType, setSelectedType] = useState(null);

  const importTypes = [
    {
      id: 'xmi',
      name: 'XMI',
      description: 'Enterprise Architect UML/XMI format',
      icon: FileCode,
      accept: '.xmi',
      color: 'blue'
    },
    {
      id: 'json',
      name: 'JSON',
      description: 'Schema Graphos JSON format',
      icon: FileJson,
      accept: '.json',
      color: 'green'
    },
    {
      id: 'sql',
      name: 'SQL DDL',
      description: 'PostgreSQL CREATE TABLE statements',
      icon: Database,
      accept: '.sql',
      color: 'purple'
    }
  ];

  const handleSelect = (type) => {
    setSelectedType(type);
  };

  const handleImport = () => {
    if (!selectedType) {
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = selectedType.accept;

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        onSelectType(selectedType.id, file);
        onClose();
      }
    };

    input.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Import Model
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select the format of the file you want to import:
          </p>

          <div className="grid grid-cols-1 gap-4">
            {importTypes.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedType?.id === type.id;

              const colorClasses = {
                blue: {
                  border: 'border-blue-500',
                  bg: 'bg-blue-50 dark:bg-blue-900/20',
                  iconBg: 'bg-blue-100 dark:bg-blue-900/40',
                  iconText: 'text-blue-600 dark:text-blue-400',
                  text: 'text-blue-700 dark:text-blue-300',
                  checkBg: 'bg-blue-500'
                },
                green: {
                  border: 'border-green-500',
                  bg: 'bg-green-50 dark:bg-green-900/20',
                  iconBg: 'bg-green-100 dark:bg-green-900/40',
                  iconText: 'text-green-600 dark:text-green-400',
                  text: 'text-green-700 dark:text-green-300',
                  checkBg: 'bg-green-500'
                },
                purple: {
                  border: 'border-purple-500',
                  bg: 'bg-purple-50 dark:bg-purple-900/20',
                  iconBg: 'bg-purple-100 dark:bg-purple-900/40',
                  iconText: 'text-purple-600 dark:text-purple-400',
                  text: 'text-purple-700 dark:text-purple-300',
                  checkBg: 'bg-purple-500'
                }
              };

              const colors = colorClasses[type.color];

              return (
                <button
                  key={type.id}
                  onClick={() => handleSelect(type)}
                  className={`flex items-start gap-4 p-4 border-2 rounded-lg transition-all text-left ${
                    isSelected
                      ? `${colors.border} ${colors.bg}`
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  <div className={`p-3 rounded-lg ${
                    isSelected
                      ? colors.iconBg
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <Icon 
                      size={24} 
                      className={isSelected ? colors.iconText : 'text-gray-600 dark:text-gray-400'}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold ${
                        isSelected
                          ? colors.text
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {type.name}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({type.accept})
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {type.description}
                    </p>
                  </div>
                  {isSelected && (
                    <div className={`w-6 h-6 rounded-full ${colors.checkBg} flex items-center justify-center`}>
                      <svg className="w-4 h-4 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={!selectedType}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {selectedType ? 'Select File' : 'Select Format First'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportSelectorModal;
